// Base
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
// Vuetify
import { createVuetify } from 'vuetify';
import 'vuetify/styles';
import { VDataTable, VDataTableServer, VSkeletonLoader } from 'vuetify/labs/components';
import { aliases, md } from 'vuetify/iconsets/md';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import '@/assets/styles/main.scss';
// VCalendar
import VCalendar from 'v-calendar';
import 'v-calendar/style.css';
// Other Plugins
import i18n from "@/plugins/vueI18n";
import ThemePlugin from '@/plugins/theme/themePlugin';
import VueCookies from "vue-cookies";
import { createGtm } from "@gtm-support/vue-gtm";
import * as amplitude from '@amplitude/analytics-browser';
import { useThemeStore } from "@/stores/theme";
import { useAppStore } from "@/stores/app";
import MouseFlowPlugin from "@/plugins/mouseflow";

import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";

// App
const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [],
});

app.use(i18n);
app.use(VueCookies);

// Pinia
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());
app.use(pinia);

// Get client data
const appStore = useAppStore();
try {
  await appStore.fetchClient();
  await appStore.fetchMembershipTiers();
} catch (e) {
  console.error(e);
  document.location.href = 'https://www.ascendfs.com/programs/change-it';
}

app.use(router);

// Theme
app.use(ThemePlugin);
const themeStore = useThemeStore();
await themeStore.fetchTheme();

// Vuetify
const vuetifyTheme = themeStore.getThemeDefinition();
// @ts-ignore
const vuetify = createVuetify({
  display: {
    mobileBreakpoint: 'md',
  },
  theme: {
    defaultTheme: 'vuetifyTheme',
    themes: {
      vuetifyTheme,
    },
  },
  defaults: {
    global: {
      ripple: false,
    },
    VBtn: {
      variant: 'flat',
      color: 'primary',
    },
    VTextField: {
      variant: 'outlined',
      color: 'primary',
    },
    VTextArea: {
      variant: 'outlined',
      color: 'primary',
    },
    VRadioGroup: {
      color: 'primary',
    },
    VSwitch: {
      color: 'primary',
      inset: true,
    },
    VSelect: {
      variant: 'outlined',
      color: 'primary',
    },
    VSlider: {
      color: 'primary',
    },
    VTab: {
      color: 'primary',
      ripple: false,
    },
    VSnackbar: {
      color: 'primary',
    },
  },
  icons: {
    defaultSet: 'md',
    aliases,
    sets: {
      md,
    },
  },
  components: {
    VDataTable,
    VDataTableServer,
    VSkeletonLoader,
  },
});
app.use(vuetify);

// VCalendar
app.use(VCalendar, {});

// GTM
if (appStore.client?.gtmId) {
  app.use(createGtm({
    id: appStore.client.gtmId,
  }));
}

// Init Amplitude
amplitude.init(import.meta.env.VITE_APP_AMPLITUDE_API_KEY);

// Init MouseFlow
app.use(MouseFlowPlugin);

// Init CSS variables from JS
document.documentElement.style.setProperty('--inner-vh', `${window.innerHeight / 100}px`);
document.documentElement.style.setProperty('--inner-vh-full', 'calc(var(--inner-vh) * 100)');

// Mount
app.mount('#app');
